import { GRAY_1000, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { FC } from 'react'
import { InvoicingAddressDTO } from 'dataQueries/invoicingAddresses.query'
import { MUICheckCard } from 'components/common/MUICards'
import { VatValidationStatusBadge } from 'components/common/Badges'
import { useTranslation } from 'react-i18next'

/**
 * Props for the InvoicingAddressCard component
 */
interface Props {
  /**
   * The invoicing address data object that will be displayed inside the card
   */
  address: InvoicingAddressDTO

  /**
   * Indicates whether the current card is selected
   */
  checked: boolean

  /**
   * Callback fired when the card is selected
   */
  onCheck: () => void
}

/**
 * Renders a selectable invoicing address card.
 *
 * This component displays:
 * - Company or individual name
 * - Street & building information
 * - City, region, postal code & country (localized)
 * - VAT number
 *
 * The card behaves like a radio input,
 * allowing only one address to be selected at a time.
 *
 * Used within the purchase flow when choosing an invoicing address.
 */
export const InvoicingAddressCard: FC<Props> = ({
  address,
  checked,
  onCheck,
}) => {

  /**
   * Translation helpers for resolving country names
   */
  const { t, i18n } = useTranslation(['country', 'purchase_flow'])

  return (
    <MUICheckCard
      isRadio
      alwaysOnFooterContent={(

        <Stack gap={0.1}>
          {/* Company or individual name */}
          <Typography fontWeight={500} color={GRAY_900}>
            {address.companyOrIndividualName}
          </Typography>

          {/* Street info */}
          <Typography>
            {address.streetAndNumber}
          </Typography>

          {/* Combined location line */}
          <Typography>
            {[
              address.apartmentNumber,
              address.city,
              address.region,
              address.postalCode,
              i18n.exists(`country:${address.country}`) ? t(`country:${address.country}`) : address.country,
            ].filter(Boolean).join(', ')}
          </Typography>

          {/* VAT number */}
          {address.vatNumber && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography>
                {t('purchase_flow:billing_step.invoicing_address_vat_field_label')}:&nbsp;
                <Typography component="span" fontWeight={500} color={GRAY_900}>{address.vatNumber}</Typography>
              </Typography>
              <VatValidationStatusBadge
                status={address.validationStatus}
                vatNumber={address.vatNumber}
                country={address.country}
                size="xs"
              />
            </Stack>
          )}
        </Stack>
      )}
      checked={checked}
      onCheck={() => onCheck()}
    >
      {/* Address name / label */}
      <Typography variant="text-md" fontWeight={500} color={GRAY_1000} sx={{ overflowWrap: 'anywhere' }}>
        {address.addressName}
      </Typography>

    </MUICheckCard>
  )
}
